import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { getLocalStorageItem } from '../../../utils/safe-storage';

@Injectable()
export class RtlDetectorService {
  /**
   * A list of languages written right-to-left.  Note that this does not use the standard
   * ISO format for labelling a language, which is aa-BB (language-country).
   * @type {string[]}
   */
  static languagesRTL = [
    'ar_AE',
    'ar_BH',
    'ar_DZ',
    'ar_EG',
    'ar_IQ',
    'ar_JO',
    'ar_KW',
    'ar_LB',
    'ar_LY',
    'ar_MA',
    'ar_OM',
    'ar_QA',
    'ar_SA',
    'ar_SY',
    'ar_TN',
    'ar_YE',
    'dv_MV',
    'fa_IR',
    'he_IL',
    'ps_AR',
    'ur_AE',
    'ur_PK',
  ];

  /**
   * A broadcast observable of the current text-direction.
   * @type {BehaviorSubject<boolean>}
   */
  private isRTL = new BehaviorSubject(false);

  constructor(translate: TranslateService) {
    // Feeds the initial RTL token into the BehaviorSubject.
    this.isRTL.next(this.activeLangRTL());
    // Monitors the translation service for language changes and updates isRTL.
    translate.onLangChange.subscribe(() => {
      this.isRTL.next(this.activeLangRTL());
    });
  }

  /**
   * Looks at the browser's local storage for the current language to show, then
   * tells us whether that language is in the list languagesRTL.
   * @return {boolean}
   */
  public activeLangRTL(): boolean {
    const lang = getLocalStorageItem('language');
    return RtlDetectorService.languagesRTL.includes(lang);
  }

  /**
   * Gives the value for the HTML "rtl" attribute, which shows the direction
   * of writing for a piece of text.
   * @return {string}
   */
  public direction(): string {
    return this.activeLangRTL() ? 'rtl' : 'ltr';
  }

  /**
   * An observable view of isRTL.
   * @return {Observable<boolean>}
   */
  public activeLangRTLObservable() {
    return this.isRTL.asObservable();
  }
}
